/**
 * todoTypes.ts — CD-20 typed todos: the ONE canonical registry of todo types.
 *
 * The codebase had the todo type defined in 3+ disagreeing places
 * (TodoDetailModal `TYPE_OPTIONS` [8], CreateTodoDialog `TYPE_OPTIONS`
 * [5], the DB trigger allow-list [8], plus the composerLabel/pickTypeIcon
 * switches). This registry is the single source of truth every todo surface
 * reads — the design-spec types with their label, action-footer verb, icon
 * key, and functional/coming-soon status.
 *
 * Owner rulings (2026-07-17): FUNCTIONAL now = todo · text · email ·
 * schedule_call · create_proposal (most are really framed email/comm actions);
 * COMING SOON = create_workflow · edit_workflow · send_form · ai_action.
 *
 * 2026-08-04: schedule_build joins the FUNCTIONAL set. It was reachable ONLY
 * via the workflow seeder — it sat in LEGACY_ALIAS, so it rendered but could
 * never be chosen, and "block time for a build" could not be added to a project
 * by hand at all. Michael: "regardless of me being pm I should still see the
 * Block time or select build slots as a task I can add to the project but I
 * don't". The seat is irrelevant to the TYPE — a PM can create the to-do; who
 * can act on it is decided inside the scheduler, by whose build grid it reads.
 *
 * NOTE: the automation ⚡ (paprika lightning bolt) is ORTHOGONAL to type — it
 * is driven by `todo.automation`, overlays any type, and is untouched here.
 */
export type TodoType =
  | 'todo'
  | 'text'
  | 'email'
  | 'phone'
  | 'schedule_call'
  | 'schedule_build'
  | 'create_proposal'
  | 'create_workflow'
  | 'edit_workflow'
  | 'send_form'
  | 'ai_action';

/**
 * Semantic icon key — decoupled from any icon library so each surface maps it
 * to its own icon component (TodoDetailModal's `I` set, UCDM's
 * pickTypeIcon, MUI). 'ai' is a distinct sparkle glyph — deliberately NOT the
 * paprika ⚡, which stays exclusive to the automation indicator.
 */
export type TodoIconKey =
  | 'todo'
  | 'text'
  | 'email'
  | 'schedule'
  | 'proposal'
  | 'workflow'
  | 'form'
  | 'ai'
  | 'build'
  | 'phone';

export interface TodoTypeDef {
  value: TodoType;
  label: string;
  /** Action-adaptive footer verb ("Compose email", "Draft proposal"). Empty
   *  for the plain 'todo' (no composer / just complete). */
  footerLabel: string;
  iconKey: TodoIconKey;
  /** false → "coming soon": shown in the picker + modal but disabled + honest
   *  (per the design system's coming-soon rule), never silently hidden. */
  functional: boolean;
}

export const TODO_TYPES: readonly TodoTypeDef[] = [
  { value: 'todo', label: 'To-do', footerLabel: '', iconKey: 'todo', functional: true },
  { value: 'text', label: 'Text', footerLabel: 'Send text', iconKey: 'text', functional: true },
  { value: 'email', label: 'Email', footerLabel: 'Compose email', iconKey: 'email', functional: true },
  // 'phone' is a REAL type, not an alias (2026-08-27, deferred 2026-08-076).
  // It was aliased onto schedule_call, so a phone to-do wore a calendar glyph
  // and a "Schedule call" footer verb -- it promised a booking EMAIL -- while
  // the row action opened the DIALER. Label and action disagreed, on a value
  // that IS in the DB trigger allow-list and therefore live. Same defect the
  // schedule_build comment below describes; this was the second one.
  { value: 'phone', label: 'Call', footerLabel: 'Open dialer', iconKey: 'phone', functional: true },
  { value: 'schedule_call', label: 'Schedule call', footerLabel: 'Schedule call', iconKey: 'schedule', functional: true },
  // A FIRST-CLASS type, not a legacy string. `schedule_build` was only ever
  // seeded by the workflow (the workflow_approved playbook row) and sat in
  // LEGACY_ALIAS below, so it rendered fine but could not be CHOSEN — there was
  // no way to add "block time for a build" to a project by hand, which is
  // exactly what the Build Block Scheduler's no-project state was built for.
  // The DB has always accepted it (todos_typed_todos_task_types_v1 lists it).
  { value: 'schedule_build', label: 'Schedule build', footerLabel: 'Open build scheduler', iconKey: 'build', functional: true },
  { value: 'create_proposal', label: 'Create proposal', footerLabel: 'Draft proposal', iconKey: 'proposal', functional: true },
  { value: 'create_workflow', label: 'Create workflow', footerLabel: 'Build workflow', iconKey: 'workflow', functional: false },
  { value: 'edit_workflow', label: 'Edit workflow', footerLabel: 'Edit workflow', iconKey: 'workflow', functional: false },
  { value: 'send_form', label: 'Send form', footerLabel: 'Send form', iconKey: 'form', functional: false },
  { value: 'ai_action', label: 'AI action', footerLabel: 'Run AI action', iconKey: 'ai', functional: false },
] as const;

const BY_VALUE = new Map<string, TodoTypeDef>(TODO_TYPES.map((t) => [t.value, t]));

/**
 * Map legacy / pre-registry task_type values (the DB still holds old rows with
 * sms/scheduling/workflow/schedule_build/call/document/stage-change, and the FE
 * union carried fire_process) onto the canonical set so old todos render with a
 * sensible icon + footer instead of falling through to a raw string.
 */
const LEGACY_ALIAS: Record<string, TodoType> = {
  sms: 'text',
  // `call` is NOT in the DB trigger allow-list, so no row can carry it -- kept
  // defensively, and now pointed at the real `phone` type rather than at a
  // booking email. `phone` is no longer an alias: it is a type (see above).
  call: 'phone',
  scheduling: 'schedule_call',
  // `schedule_build` was here — wrongly. It is not a legacy value: the workflow
  // seeds it TODAY and it has its own entry above. Aliasing it onto
  // schedule_call gave build to-dos a calendar glyph and a "Schedule call"
  // footer verb, and kept it out of the picker entirely.
  document: 'send_form',
  form: 'send_form',
  workflow: 'create_workflow',
  fire_process: 'create_workflow',
  'stage-change': 'todo',
};

/** Resolve any stored/legacy value to its canonical TodoTypeDef (falls back to
 *  'todo' for unknown values so the UI never breaks on a novel string). */
export function todoTypeDef(value: string | null | undefined): TodoTypeDef {
  if (value && BY_VALUE.has(value)) return BY_VALUE.get(value)!;
  const aliased = value ? LEGACY_ALIAS[value] : undefined;
  return (aliased && BY_VALUE.get(aliased)) || BY_VALUE.get('todo')!;
}

/** The canonical value set the DB trigger allow-list must accept (see the
 *  frozen database.ts widen migration). */
export const CANONICAL_TODO_TYPE_VALUES: readonly TodoType[] = TODO_TYPES.map((t) => t.value);
